import { BaseReportHandler } from "./BaseReportHandler";
import { ExcelBuilder } from "../utils/ExcelBuilder";
import { PdfBuilder } from "../utils/PdfBuilder";
import { lang } from "../../../utils/langs";

export class TaxReportHandler extends BaseReportHandler {
    
    getColumns(sectionName) {
        if (this.metadata.report_type === 'tax_report') {
            return {
                headers: ['Fecha', 'Número', 'NIT/CC', 'Tercero', 'Cuenta', 'Base Gravable', 'Impuesto'],
                keys: ['fecha', 'comprobante', 'identificacion', 'nombre_tercero', 'account', 'base', 'tax']
            };
        }
        
        if (this.metadata.report_type === 'sales_report' && sectionName === 'sales_detail_journal') {
            const taxCols = this.metadata.tax_columns || [];
            return {
                headers: ['Fecha', 'Número', 'Tipo', 'Cliente', 'Base Gravable', 'Base Exenta', ...taxCols, 'Total'],
                keys: ['fecha', 'documento', 'tipo', 'cliente', 'base_gravable', 'base_exenta', 'impuestos', 'total']
            };
        }

        if (this.metadata.report_type === 'purchase_report' && sectionName === 'purchase_detail_journal') {
            const taxCols = this.metadata.tax_columns || [];
            return {
                headers: ['Fecha', 'Número', 'Proveedor', 'Base Gravable', 'Base Exenta', ...taxCols, 'Total'],
                keys: ['fecha', 'documento', 'proveedor', 'base_gravable', 'base_exenta', 'impuestos', 'total']
            };
        }

        return { headers: [], keys: [] };
    }

    async downloadExcel() {
        const builder = new ExcelBuilder(this.metadata);
        builder.addMainHeader();

        this.sections.forEach(section => {
            if (section.name === 'sales_summary_daily') return;

            builder.addSectionTitle(section.name || "Detalle");
            const { headers, keys } = this.getColumns(section.name);
            const rows = section.data || [];

            const currencyKeys = ['base', 'tax', 'base_gravable', 'base_exenta', 'total', ...(this.metadata.tax_columns || [])];
            const currencyIndices = [];
            
            const dataRows = rows.map(item => {
                let colIdx = 0;
                return keys.flatMap(k => {
                    if (k === 'impuestos') {
                        return (this.metadata.tax_columns || []).map(tc => {
                            if (!currencyIndices.includes(colIdx)) currencyIndices.push(colIdx);
                            colIdx++;
                            return item.impuestos ? item.impuestos[tc] : 0;
                        });
                    }
                    if (currencyKeys.includes(k)) {
                        if (!currencyIndices.includes(colIdx)) currencyIndices.push(colIdx);
                    }
                    colIdx++;
                    return [item[k] ?? ""];
                });
            });

            // Re-calculate headers for flat array
            let flatHeaders = headers;

            builder.addTable(flatHeaders, dataRows, {
                onRowAdded: (excelRow) => {
                    excelRow.eachCell((cell, colNumber) => {
                        if (currencyIndices.includes(colNumber - 1) && typeof cell.value === 'number') {
                            cell.numFmt = '"$"#,##0;[Red]"-"$#,##0';
                            cell.alignment = { horizontal: "right" };
                        }
                    });
                }
            });

            // Add Totals Row
            const flatKeys = keys.flatMap(k => k === 'impuestos' ? (this.metadata.tax_columns || []) : [k]);
            const flatCurrencyKeys = flatKeys.filter(k => currencyKeys.includes(k) || (this.metadata.tax_columns || []).includes(k));
            
            // We need to flatten the data to calculate totals correctly because of 'impuestos'
            const flatData = rows.map(item => {
                const obj = {};
                keys.forEach(k => {
                    if (k === 'impuestos') {
                        (this.metadata.tax_columns || []).forEach(tc => {
                            obj[tc] = item.impuestos ? item.impuestos[tc] : 0;
                        });
                    } else {
                        obj[k] = item[k];
                    }
                });
                return obj;
            });

            const totalsRowValues = this.calculateTotals(flatData, flatKeys, flatCurrencyKeys);
            builder.addTotalsRow(totalsRowValues, currencyIndices);

            builder.addRow([]);
        });

        await builder.download();
    }

    async downloadPdf(download = true) {
        const builder = new PdfBuilder(this.metadata);
        builder.setOrientation("landscape");
        builder.addMainHeader();

        this.sections.forEach(section => {
            if (section.name === 'sales_summary_daily') return;

            builder.addSectionHeader(section.name || "Detalle");
            const { headers, keys } = this.getColumns(section.name);
            const rows = section.data || [];

            const currencyKeys = ['base', 'tax', 'base_gravable', 'base_exenta', 'total', ...(this.metadata.tax_columns || [])];
            const currencyIndices = [];

            const bodyRows = rows.map(item => {
                let colIdx = 0;
                const values = keys.flatMap(k => {
                    if (k === 'impuestos') {
                        return (this.metadata.tax_columns || []).map(tc => {
                            if (!currencyIndices.includes(colIdx)) currencyIndices.push(colIdx);
                            colIdx++;
                            return item.impuestos ? item.impuestos[tc] : 0;
                        });
                    }
                    if (currencyKeys.includes(k)) {
                        if (!currencyIndices.includes(colIdx)) currencyIndices.push(colIdx);
                    }
                    colIdx++;
                    return [item[k]];
                });

                return values.map((val, i) => {
                    const isCurrency = currencyIndices.includes(i);
                    return {
                        text: isCurrency ? builder.formatCurrency(val) : builder.formatGeneralValue(val),
                        alignment: isCurrency ? "right" : "left",
                        noWrap: isCurrency
                    };
                });
            });

            // Add Totals Row for PDF
            const flatKeys = keys.flatMap(k => k === 'impuestos' ? (this.metadata.tax_columns || []) : [k]);
            const flatCurrencyKeys = flatKeys.filter(k => currencyKeys.includes(k) || (this.metadata.tax_columns || []).includes(k));
            const flatData = rows.map(item => {
                const obj = {};
                keys.forEach(k => {
                    if (k === 'impuestos') {
                        (this.metadata.tax_columns || []).forEach(tc => {
                            obj[tc] = item.impuestos ? item.impuestos[tc] : 0;
                        });
                    } else {
                        obj[k] = item[k];
                    }
                });
                return obj;
            });

            const totalsRowValues = this.calculateTotals(flatData, flatKeys, flatCurrencyKeys);
            bodyRows.push(builder.getTotalsRow(totalsRowValues, currencyIndices));

            builder.addTable(headers, bodyRows);
        });

        return builder.download(download);
    }
}
